/* eslint-disable @typescript-eslint/no-explicit-any */
import { ComponentType, ReactNode } from 'react';

/**
 * Higher-order component (HOC) that provides a store context to a component.
 *
 * @flutter_template_ci MainStore - The type of the main store class.
 *
 * @param mainStoreInstance - The main store instance to be provided.
 * @param StoreProvider - The provider component that accepts the main store instance and renders children.
 *
 * @returns A function that takes a component and returns a new component wrapped with the store provider.
 *
 * @example
 * ```tsx
 * const EnhancedComponent = withStoreProvider(
 *  new Store(),
 *  StoreProvider
 * )(MyComponent);
 * ```
 */
function withStoreProvider<MainStore extends new (...args: any[]) => any>(
  mainStoreInstance: InstanceType<MainStore>,
  StoreProvider: ComponentType<{
    store: InstanceType<MainStore>;
    children: ReactNode;
  }>,
) {
  return function <P extends object>(Component: ComponentType<P>) {
    return (props: P) => {
      return (
        <StoreProvider store={mainStoreInstance}>
          <Component {...props} />
        </StoreProvider>
      );
    };
  };
}

export default withStoreProvider;
